import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { plants as plantsApi } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useReturnableRoute } from "@/hooks/useReturnableRoute";
import DuplicatesHeader from "@/components/duplicates/DuplicatesHeader";
import DuplicateGroup from "@/components/duplicates/DuplicateGroup";
import ConfirmDeleteDialog from "@/components/duplicates/ConfirmDeleteDialog";
import ConfirmMergeDialog from "@/components/duplicates/ConfirmMergeDialog";

/**
 * Duplicates — slim orchestrator. The heavy lifting lives in
 * `@/components/duplicates/*`; this page owns the data-fetch, the
 * per-scan exclusion Set, and the action handlers (delete / merge /
 * dismiss / restore) that mutate them.
 *
 * Two modes toggled via `?mode=species|exact`:
 *  • exact  → byte-identical duplicates. Only "delete extras" makes
 *    sense here (there's no partial-select concept), so we render a
 *    trash button per row and skip merge UI entirely.
 *  • species → visually distinct scans classified as the same plant.
 *    Users pick which scans to keep, merge collapses them into the
 *    oldest (primary), and "keep separate" dismisses the group.
 */
export default function Duplicates() {
  const { t, i18n } = useTranslation();
  const openDetail = useReturnableRoute();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") === "species" ? "species" : "exact";

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [busyScanId, setBusyScanId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // { scan_id, label }
  const [pendingMerge, setPendingMerge] = useState(null);   // { groupKey, primary_scan_id, merge_ids, label, total }
  const [mergingKey, setMergingKey] = useState(null);
  // Per-group exclusion: entries in the Set are scan_ids the user has
  // UNCHECKED (i.e. "leave this one separate"). Storing exclusions
  // (not inclusions) means the merge action defaults to including
  // everything — matching the pre-refactor behavior for groups the
  // user hasn't touched.
  const [excludedByGroup, setExcludedByGroup] = useState({}); // { [groupKey]: Set<scan_id> }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = mode === "species"
        ? await plantsApi.nearDuplicates()
        : await plantsApi.duplicates();
      setGroups(data?.groups || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("duplicates.loadFail"));
    } finally {
      setLoading(false);
    }
  }, [t, mode]);

  useEffect(() => { load(); }, [load]);

  const totalDupes = useMemo(
    () => groups.reduce((n, g) => (g.dismissed ? n : n + Math.max(0, (g.count || 0) - 1)), 0),
    [groups]
  );
  const activeGroupCount = useMemo(
    () => groups.filter((g) => !g.dismissed).length,
    [groups]
  );
  const dismissedGroupCount = useMemo(
    () => groups.filter((g) => g.dismissed).length,
    [groups]
  );

  // --- Exclusion helpers ---------------------------------------------------
  const toggleExclude = (groupKey, scanId) => {
    setExcludedByGroup((prev) => {
      const nextSet = new Set(prev[groupKey] || []);
      if (nextSet.has(scanId)) nextSet.delete(scanId); else nextSet.add(scanId);
      return { ...prev, [groupKey]: nextSet };
    });
  };

  const toggleAll = (groupKey, group, allIncluded) => {
    setExcludedByGroup((prev) => {
      if (allIncluded) {
        // Exclude everything EXCEPT the primary so the group isn't empty.
        const next = new Set((group.scans || []).slice(1).map((s) => s.scan_id));
        return { ...prev, [groupKey]: next };
      }
      return { ...prev, [groupKey]: new Set() };
    });
  };

  // --- Actions -------------------------------------------------------------
  const confirmDelete = async () => {
    const s = pendingDelete;
    if (!s) return;
    setPendingDelete(null);
    setBusyScanId(s.scan_id);
    try {
      await plantsApi.remove(s.scan_id);
      toast.success(t("duplicates.deletedOne"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("duplicates.deleteFail"));
    } finally {
      setBusyScanId(null);
    }
  };

  const confirmMerge = async () => {
    const m = pendingMerge;
    if (!m) return;
    setPendingMerge(null);
    setMergingKey(m.groupKey);
    try {
      const { data } = await plantsApi.merge(m.primary_scan_id, m.merge_ids);
      const dropped = data?.extra_images_dropped || 0;
      toast.success(
        dropped > 0
          ? t("duplicates.mergedWithDropped", { count: data.merged_count, dropped })
          : t("duplicates.mergedOne", { count: data.merged_count })
      );
      // Drop any per-row exclusions for the merged group — reload will refresh IDs.
      setExcludedByGroup((prev) => {
        const next = { ...prev };
        delete next[m.groupKey];
        return next;
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("duplicates.mergeFail"));
    } finally {
      setMergingKey(null);
    }
  };

  const dismissGroup = async (group) => {
    const speciesKey = group.species_key;
    if (!speciesKey) return;
    // Optimistically mark the group as dismissed in-place so it sinks
    // to the bottom with the "previously dismissed" badge — but stays
    // visible so the user can change their mind.
    setGroups((prev) => {
      const marked = prev.map((g) =>
        g.species_key === speciesKey ? { ...g, dismissed: true } : g
      );
      return marked.slice().sort(
        (a, b) =>
          Number(!!a.dismissed) - Number(!!b.dismissed) ||
          (b.count || 0) - (a.count || 0)
      );
    });
    try {
      await plantsApi.dismissNearDup(speciesKey);
      toast.success(
        t("duplicates.dismissed", { name: group.common_name || group.scientific_name || "" }),
        {
          action: {
            label: t("common.undo"),
            onClick: async () => {
              try {
                await plantsApi.undismissNearDup(speciesKey);
                toast.success(t("duplicates.undismissed"));
                await load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || t("duplicates.undismissFail"));
              }
            },
          },
          duration: 8000,
        }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("duplicates.dismissFail"));
      await load();
    }
  };

  const restoreGroup = async (group) => {
    const speciesKey = group.species_key;
    if (!speciesKey) return;
    // Optimistic un-dismiss — flip the flag in place so the group
    // jumps back to the active section without a full-reload flicker.
    setGroups((prev) => {
      const marked = prev.map((g) =>
        g.species_key === speciesKey ? { ...g, dismissed: false } : g
      );
      return marked.slice().sort(
        (a, b) =>
          Number(!!a.dismissed) - Number(!!b.dismissed) ||
          (b.count || 0) - (a.count || 0)
      );
    });
    try {
      await plantsApi.undismissNearDup(speciesKey);
      toast.success(t("duplicates.restored", { name: group.common_name || group.scientific_name || "" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("duplicates.undismissFail"));
      await load();
    }
  };

  return (
    <AppShell>
      <div className="md:max-w-3xl md:mx-auto">
        <DuplicatesHeader
          mode={mode}
          loading={loading}
          activeGroupCount={activeGroupCount}
          dismissedGroupCount={dismissedGroupCount}
          totalDupes={totalDupes}
        />

        {!loading && activeGroupCount === 0 && dismissedGroupCount === 0 && (
          <div className="mt-10 mx-5 rounded-3xl bg-parchment/60 p-6 text-center" data-testid="duplicates-none">
            <p className="text-leaf-ink font-medium">
              {mode === "species" ? t("duplicates.speciesEmptyTitle") : t("duplicates.emptyTitle")}
            </p>
            <p className="mt-1 text-sm text-leaf-mist">
              {mode === "species" ? t("duplicates.speciesEmptyDesc") : t("duplicates.emptyDesc")}
            </p>
          </div>
        )}

        {!loading && groups.length > 0 && (
          <div className="mt-4 px-5 space-y-4 pb-24" data-testid="duplicates-groups">
            {groups.map((g, gi) => {
              const groupKey = g.image_hash || g.species_key || String(gi);
              const excludedSet = excludedByGroup[groupKey] || new Set();
              const showDismissedDivider =
                mode === "species" && g.dismissed && gi > 0 && !groups[gi - 1]?.dismissed;
              return (
                <DuplicateGroup
                  key={groupKey}
                  group={g}
                  gi={gi}
                  groupKey={groupKey}
                  mode={mode}
                  showDismissedDivider={showDismissedDivider}
                  excludedSet={excludedSet}
                  isMerging={mergingKey === groupKey}
                  busyScanId={busyScanId}
                  language={user?.language}
                  locale={i18n?.language}
                  onToggleExclude={toggleExclude}
                  onToggleAll={toggleAll}
                  onDismiss={dismissGroup}
                  onRestore={restoreGroup}
                  onRequestMerge={setPendingMerge}
                  onRequestDelete={setPendingDelete}
                  onOpenDetail={(scan) => openDetail(`/plant/${scan.scan_id}`, { scan })}
                />
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        pending={pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />

      <ConfirmMergeDialog
        pending={pendingMerge}
        onCancel={() => setPendingMerge(null)}
        onConfirm={confirmMerge}
      />
    </AppShell>
  );
}
